import { immer } from "zustand/middleware/immer";
import { createStore } from "zustand/vanilla";

import { createGroupsSlice } from "./groups.slice";
import { createTicketRoutingSlice } from "./ticketRouting.slice";
import { createWidgetSettingsSlice } from "./widgetSettings.slice";
import { createWidgetsSlice } from "./widgets.slice";
import type { WorkspaceSnapshot, WorkspaceStore } from "./workspace.types";

export interface CreateWorkspaceStoreOptions {
  snapshot?: WorkspaceSnapshot;
}

/**
 * Creates an independent vanilla Zustand + Immer workspace store for a single
 * mounted workspace tab. Compose the groups, widgets/placements, and
 * ticket-routing slices in one Immer transaction surface so any slice can
 * mutate the full draft.
 *
 * Pass a snapshot to synchronously hydrate before returning the store.
 */
export const createWorkspaceStore = (
  options: CreateWorkspaceStoreOptions = {},
) => {
  const store = createStore<WorkspaceStore>()(
    immer((...args) => ({
      ...createGroupsSlice(...args),
      ...createWidgetsSlice(...args),
      ...createWidgetSettingsSlice(...args),
      ...createTicketRoutingSlice(...args),
    })),
  );

  if (options.snapshot) {
    store.getState().applySnapshot(options.snapshot);
  }

  return store;
};
